<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center justify-between">
        <div>
          <div class="flex items-center mb-2">
            <a
              href="/admin/job-posts"
              class="text-red-900 hover:text-red-800 flex items-center mr-4"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Job Posts
            </a>
          </div>
          <h1 class="text-2xl font-bold text-gray-900">
            {{ isEditing ? 'Edit Job Post' : 'Job Post Details' }}
          </h1>
          <p class="mt-2 text-sm text-gray-700">
            {{ isEditing ? 'Update the job post information below.' : 'View and manage this job post.' }}
          </p>
        </div>
        <div class="flex items-center space-x-3">
          <button
            v-if="!isEditing"
            @click="startEditing"
            class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Edit Job Post
          </button>
          <button
            v-if="isEditing"
            @click="cancelEditing"
            class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            v-if="isEditing"
            @click="saveJobPost"
            :disabled="isSaving"
            class="bg-red-900 text-white px-4 py-2 rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSaving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-8">
      <div class="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm text-white bg-red-900 rounded-md">
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading...
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="errorMessage" class="text-center py-8">
      <div class="text-red-600">{{ errorMessage }}</div>
      <button
        @click="fetchJobPost"
        class="mt-4 bg-red-900 text-white px-4 py-2 rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
      >
        Try Again
      </button>
    </div>

    <!-- Job Post Content -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <!-- Main Content -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Job Information -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">Job Information</h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
              <input
                v-if="isEditing"
                v-model="jobForm.title"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="e.g., Senior Software Engineer"
              />
              <p v-else class="text-gray-900">{{ jobPost.title }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <input
                v-if="isEditing"
                v-model="jobForm.company"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="e.g., TechCorp International"
              />
              <p v-else class="text-gray-900">{{ jobPost.company }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Job Link (optional)</label>
              <input
                v-if="isEditing"
                v-model="jobForm.link"
                type="url"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="https://company.com/careers/job-title"
              />
              <p v-else class="text-gray-900">
                <a
                  v-if="jobPost.link"
                  :href="jobPost.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-red-900 hover:text-red-800"
                >
                  {{ jobPost.link }}
                </a>
                <span v-else class="text-gray-500">No link provided</span>
              </p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                v-if="isEditing"
                v-model="jobForm.status"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="draft">Draft</option>
              </select>
              <span
                v-else
                :class="getStatusClass(jobPost.status)"
                class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              >
                {{ jobPost.status }}
              </span>
            </div>
          </div>

          <div class="mt-6">
            <label class="block text-sm font-medium text-gray-700 mb-2">Job Description</label>
            <textarea
              v-if="isEditing"
              v-model="jobForm.description"
              rows="6"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Describe the role, requirements, location, salary range, or any other relevant details..."
            ></textarea>
            <p v-else class="text-gray-900 whitespace-pre-wrap">{{ jobPost.description }}</p>
          </div>
        </div>

        <!-- Poster Information -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">Poster Information</h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
              <input
                v-if="isEditing"
                v-model="jobForm.posterName"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="How should we credit you for sharing this job?"
              />
              <p v-else class="text-gray-900">{{ jobPost.posterName }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Your Email (optional)</label>
              <input
                v-if="isEditing"
                v-model="jobForm.posterEmail"
                type="email"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="your.email@example.com"
              />
              <p v-else class="text-gray-900">
                {{ jobPost.posterEmail || 'No email provided' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Job Statistics -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">Job Statistics</h2>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="text-center">
              <div class="text-2xl font-bold text-blue-600">{{ jobPost.views || 0 }}</div>
              <div class="text-sm text-gray-500">Total Views</div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-bold text-green-600">{{ jobPost.applications || 0 }}</div>
              <div class="text-sm text-gray-500">Applications</div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-bold text-purple-600">{{ jobPost.saves || 0 }}</div>
              <div class="text-sm text-gray-500">Times Saved</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Job Actions -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Actions</h3>

          <div class="space-y-3">
            <button
              @click="viewPublicJob"
              class="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              View Public Job
            </button>

            <button
              @click="duplicateJob"
              class="w-full bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Duplicate Job
            </button>

            <button
              @click="deleteJob"
              class="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Delete Job
            </button>
          </div>
        </div>

        <!-- Job Details -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Job Details</h3>

          <div class="space-y-3">
            <div>
              <span class="text-sm font-medium text-gray-700">Posted:</span>
              <p class="text-gray-900">{{ formatDate(jobPost.postedDate) }}</p>
            </div>

            <div>
              <span class="text-sm font-medium text-gray-700">Last Updated:</span>
              <p class="text-gray-900">{{ formatDate(jobPost.updatedDate || jobPost.postedDate) }}</p>
            </div>

            <div>
              <span class="text-sm font-medium text-gray-700">Job ID:</span>
              <p class="text-gray-900 font-mono text-sm">{{ jobPost.id }}</p>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>

          <div class="space-y-3">
            <button
              @click="toggleJobStatus"
              class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
            >
              {{ jobPost.status === 'active' ? 'Mark as Expired' : 'Mark as Active' }}
            </button>

            <button
              @click="copyJobLink"
              class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
            >
              Copy Job Link
            </button>

            <button
              @click="shareJob"
              class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
            >
              Share Job
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

// Data
const isLoading = ref(true)
const errorMessage = ref('')
const isEditing = ref(false)
const isSaving = ref(false)
const jobPost = ref({})
const jobForm = ref({})

// Sample data (replace with API calls)
const sampleJobPost = {
  id: 1,
  title: 'Senior Software Engineer',
  company: 'TechCorp International',
  description: 'Looking for a senior developer to join our Berlin team. Remote work possible, competitive salary, great benefits. Experience with React, Node.js, and cloud platforms preferred. We offer flexible working hours, professional development opportunities, and a collaborative team environment.',
  link: 'https://techcorp.com/careers/senior-engineer',
  posterName: 'John Doe',
  posterEmail: 'john.doe@email.com',
  status: 'active',
  postedDate: '2024-01-15',
  updatedDate: '2024-01-15',
  views: 45,
  applications: 8,
  saves: 12
}

// Methods
const fetchJobPost = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // In a real app, this would make an API call using the job post ID
    await new Promise(resolve => setTimeout(resolve, 1000))

    jobPost.value = sampleJobPost
    jobForm.value = { ...sampleJobPost }
  } catch (error) {
    errorMessage.value = 'Failed to fetch job post. Please try again.'
    console.error('Error fetching job post:', error)
  } finally {
    isLoading.value = false
  }
}

const startEditing = () => {
  isEditing.value = true
  jobForm.value = { ...jobPost.value }
}

const cancelEditing = () => {
  isEditing.value = false
  jobForm.value = { ...jobPost.value }
}

const saveJobPost = async () => {
  isSaving.value = true

  try {
    // In a real app, this would make an API call to update the job post
    await new Promise(resolve => setTimeout(resolve, 1000))

    jobPost.value = { ...jobForm.value, updatedDate: new Date().toISOString().split('T')[0] }
    isEditing.value = false

    // Show success message
    alert('Job post updated successfully!')
  } catch (error) {
    console.error('Error updating job post:', error)
    alert('Failed to update job post. Please try again.')
  } finally {
    isSaving.value = false
  }
}

const viewPublicJob = () => {
  // In a real app, this would redirect to the public job view
  window.open(`/jobs/${jobPost.value.id}`, '_blank')
}

const duplicateJob = () => {
  if (confirm('Create a copy of this job post?')) {
    // In a real app, this would create a duplicate job post
    alert('Job post duplicated! You can now edit the copy.')
  }
}

const deleteJob = () => {
  if (confirm('Are you sure you want to delete this job post? This action cannot be undone.')) {
    // In a real app, this would make an API call to delete the job post
    router.push('/admin/job-posts')
  }
}

const toggleJobStatus = () => {
  const newStatus = jobPost.value.status === 'active' ? 'expired' : 'active'

  if (confirm(`Mark this job as ${newStatus}?`)) {
    // In a real app, this would make an API call to update the status
    jobPost.value.status = newStatus
    alert(`Job status updated to ${newStatus}`)
  }
}

const copyJobLink = () => {
  const jobUrl = `${window.location.origin}/jobs/${jobPost.value.id}`
  navigator.clipboard.writeText(jobUrl)
  alert('Job link copied to clipboard!')
}

const shareJob = () => {
  if (navigator.share) {
    navigator.share({
      title: jobPost.value.title,
      text: `Check out this job opportunity: ${jobPost.value.title} at ${jobPost.value.company}`,
      url: `${window.location.origin}/jobs/${jobPost.value.id}`
    })
  } else {
    copyJobLink()
  }
}

const getStatusClass = (status) => {
  const statusClasses = {
    active: 'bg-green-100 text-green-800',
    expired: 'bg-yellow-100 text-yellow-800',
    draft: 'bg-gray-100 text-gray-800'
  }
  return statusClasses[status] || 'bg-gray-100 text-gray-800'
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'

  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Lifecycle
onMounted(() => {
  fetchJobPost()
})
</script>
